import type {
  FieldGroupErrorType,
  InputType,
} from '@/app/components/forms/field-group';
import {
  type CustomFormStyleConfig,
  getCustomFormStyle,
  labelRequiredStyled,
  labelSecondaryTextStyled,
  labelTextStyled,
} from '@/app/components/forms/styles';
import type { Theme } from '@/app/types/styles/theme';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export interface FieldGroupLabelType {
  inputType?: InputType;
  /**
   * Label of field group
   */
  label?: string;
  /**
   * HTML for attribute to support auto focus usability with input id
   */
  htmlFor?: string;
  /**
   * (Optional) Secondary label next to the label of field group
   */
  secondaryLabel?: string;
  /**
   * (Optional) Is this a required field or not?
   */
  required?: boolean;
  /**
   * Default theme styling
   */
  theme?: Theme;
}

interface FieldGroupLabelProps
  extends FieldGroupLabelType,
    FieldGroupErrorType {
  /**
   * Custom styling
   */
  customStyle?: CustomFormStyleConfig['fieldGroup'];
}

export const FieldGroupLabel = ({
  inputType = 'input',
  htmlFor,
  label,
  secondaryLabel,
  required = false,
  theme = 'light',
  customStyle,
  ...rest
}: FieldGroupLabelProps) => {
  const labelClass = cn(
    'text-body-1-fixed font-noto-bold block',
    labelTextStyled({ theme }),
    getCustomFormStyle(customStyle?.label)
  );

  const secondaryClass = cn(
    'ml-1 inline',
    labelSecondaryTextStyled({ theme }),
    getCustomFormStyle(customStyle?.secondary)
  );

  const requiredClass = cn(
    'ml-1 inline',
    labelRequiredStyled({ theme }),
    getCustomFormStyle(customStyle?.required)
  );

  const Component = inputType === 'control' ? 'legend' : 'label';

  return (
    <Component
      {...(Component === 'label' && {
        htmlFor,
        'aria-labelledby': htmlFor,
      })}
      {...(Component === 'legend' && !!htmlFor && { id: `${htmlFor}-label` })}
      className={labelClass}
      {...createDataAttributes({ test: 'label' })}
      {...rest}
    >
      {label}
      {!!secondaryLabel && (
        <span
          className={secondaryClass}
          {...createDataAttributes({ test: `secondary-label` })}
        >
          {secondaryLabel}
        </span>
      )}
      {!!required && (
        <span
          className={requiredClass}
          {...createDataAttributes({ test: 'required' })}
        >
          *
        </span>
      )}
    </Component>
  );
};
